import { FC } from 'react';

import { ArrowRight, PackageMinus } from 'lucide-react';

import { deliveryMethodLabel, formatOrderAmount } from './orderRevisionUtils';
import { Image } from '../../../components/ui/Image';
import { OrderRevision } from '../../../types';

interface Props {
    revision: OrderRevision;
}

export const OrderRevisionDiff: FC<Props> = ({ revision }) => {
    const changedItems = revision.items.filter((item) => item.changed);

    return (
        <div className="space-y-4">
            <section>
                <h4 className="mb-2 text-sm font-semibold">Товари</h4>
                {changedItems.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                        Склад замовлення без змін.
                    </p>
                ) : (
                    <div className="space-y-2">
                        {changedItems.map((item) => (
                            <div
                                key={item.id}
                                className="flex items-center gap-3 rounded-xl border bg-background/70 p-3"
                            >
                                <div className="h-14 w-10 flex-shrink-0 overflow-hidden rounded-md bg-muted">
                                    <Image
                                        src={
                                            item.product_image_url ||
                                            '/placeholder.svg'
                                        }
                                        alt={item.product_name}
                                        className="object-cover"
                                    />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-medium">
                                        {item.product_name}
                                    </p>
                                    {item.expansion_name && (
                                        <p className="truncate text-xs text-muted-foreground">
                                            {item.expansion_name}
                                        </p>
                                    )}
                                    {item.removed ? (
                                        <p className="mt-1 flex items-center gap-1 text-sm font-medium text-destructive">
                                            <PackageMinus className="h-4 w-4" />
                                            Видалено із замовлення
                                        </p>
                                    ) : (
                                        <p className="mt-1 flex items-center gap-2 text-sm">
                                            Кількість:
                                            <span className="font-semibold">
                                                {item.old_quantity}
                                            </span>
                                            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                                            <span className="font-semibold text-primary">
                                                {item.new_quantity}
                                            </span>
                                        </p>
                                    )}
                                </div>
                                <div className="text-right text-xs text-muted-foreground">
                                    {formatOrderAmount(item.unit_price)} / шт.
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <div className="grid gap-3 sm:grid-cols-2">
                <section className="rounded-xl border bg-background/70 p-3">
                    <h4 className="mb-2 text-xs font-medium text-muted-foreground">
                        Доставка
                    </h4>
                    <div className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                        <span>
                            {deliveryMethodLabel(revision.old_delivery_method)}
                        </span>
                        {revision.old_delivery_method !==
                            revision.new_delivery_method && (
                            <>
                                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                                <span className="text-primary">
                                    {deliveryMethodLabel(
                                        revision.new_delivery_method,
                                    )}
                                </span>
                            </>
                        )}
                    </div>
                </section>

                <section className="rounded-xl border bg-background/70 p-3">
                    <h4 className="mb-2 text-xs font-medium text-muted-foreground">
                        Сума
                    </h4>
                    <div className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                        <span>{formatOrderAmount(revision.old_total)}</span>
                        {revision.old_total !== revision.new_total && (
                            <>
                                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                                <span className="text-primary">
                                    {formatOrderAmount(revision.new_total)}
                                </span>
                            </>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
};
